import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playersAPI, API_URL } from '../services/api';
import './PlayerProfile.css';

const PlayerProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [playerData, setPlayerData] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    playerPassword: '',
    guardian1Password: '',
    guardian2Password: '',
  });

  // Determine which guardian is logged in (if any)
  const isGuardian1 = user?.role === 'guardian' && user?.guardianNumber === 1;
  const isGuardian2 = user?.role === 'guardian' && user?.guardianNumber === 2;
  const isPlayer = user?.role === 'player';

  useEffect(() => {
    if (!user?.player?.id) {
      setError('Player information not found');
      setLoading(false);
      return;
    }

    loadPlayerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.player?.id]);

  const loadPlayerData = async () => {
    try {
      setLoading(true);
      const response = await playersAPI.getDashboard(user?.player?.id);
      const player = response.data.player;
      setPlayerData(player);
      
      setFormData({
        firstName: player.firstName || '',
        lastName: player.lastName || '',
        dateOfBirth: player.dateOfBirth ? player.dateOfBirth.split('T')[0] : '',
        playerPassword: '',
        guardian1Password: '',
        guardian2Password: '',
      });
      
      if (player.photoUrl) {
        setPhotoPreview(`${API_URL}${player.photoUrl}`);
      }
      
      setError('');
    } catch (err: any) {
      console.error('Failed to load player data:', err);
      setError(err.response?.data?.message || 'Failed to load player data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Prepare update data based on permissions
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
      };

      // Only include passwords if they're filled in and user has permission
      if (isPlayer && formData.playerPassword) {
        updateData.playerPassword = formData.playerPassword;
      }
      
      if (isGuardian1 && formData.guardian1Password) {
        updateData.guardian1Password = formData.guardian1Password;
      }
      
      if (isGuardian2 && formData.guardian2Password) {
        updateData.guardian2Password = formData.guardian2Password;
      }

      // Update profile data
      await playersAPI.updateProfile(user?.player?.id, updateData);

      // Upload photo if selected
      if (photoFile) {
        await playersAPI.uploadPhoto(user?.player?.id, photoFile);
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        navigate('/player/dashboard');
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/player/dashboard');
  };

  if (loading) {
    return (
      <div className="player-profile">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="player-profile">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate(-1)} className="btn-back">Go Back</button>
      </div>
    );
  }

  if (!playerData) {
    return (
      <div className="player-profile">
        <div className="error-message">Player data not found. Please try refreshing the page.</div>
        <button onClick={() => window.location.reload()} className="btn-refresh">Refresh</button>
        <button onClick={() => navigate(-1)} className="btn-back">Go Back</button>
      </div>
    );
  }

  return (
    <div className="player-profile">
      <div className="profile-header">
        <h1>Edit Profile</h1>
        <p>Update your personal information</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="profile-form">
        {/* Photo Upload Section */}
        <div className="form-section">
          <h2>Profile Photo</h2>
          <div className="photo-upload-section">
            <div className="photo-preview">
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" />
              ) : (
                <div className="photo-placeholder">No Photo</div>
              )}
            </div>
            <div className="photo-upload-controls">
              <label htmlFor="photo" className="btn-upload">
                Choose Photo
                <input
                  type="file"
                  id="photo"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="upload-hint">Upload a profile photo (JPG, PNG)</p>
            </div>
          </div>
        </div>

        {/* Personal Information - Editable Fields */}
        <div className="form-section">
          <h2>Personal Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
            />
          </div>

          {/* Read-only Admin Fields */}
          <div className="form-row">
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                value={playerData.position || 'Not set'}
                disabled
                className="readonly-field"
              />
              <small className="field-note">Only admin can edit</small>
            </div>

            <div className="form-group">
              <label>Jersey Number</label>
              <input
                type="text"
                value={playerData.jerseyNumber || 'Not set'}
                disabled
                className="readonly-field"
              />
              <small className="field-note">Only admin can edit</small>
            </div>
          </div>
        </div>

        {/* Player Login Password - Only editable by player */}
        {isPlayer && (
          <div className="form-section">
            <h2>Player Login</h2>
            <div className="form-group">
              <label htmlFor="playerPassword">Change Password</label>
              <input
                type="password"
                id="playerPassword"
                name="playerPassword"
                value={formData.playerPassword}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
              />
              <small className="field-note">Enter new password only if you want to change it</small>
            </div>
          </div>
        )}

        {/* Guardian 1 Information */}
        <div className="form-section">
          <h2>Guardian 1 Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={playerData.guardian1Name || 'Not set'}
                disabled
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={playerData.guardian1Email || 'Not set'}
                disabled
                className="readonly-field"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={playerData.guardian1Phone || 'Not set'}
                disabled
                className="readonly-field"
              />
            </div>

            {isGuardian1 && (
              <div className="form-group">
                <label htmlFor="guardian1Password">Change Your Password</label>
                <input
                  type="password"
                  id="guardian1Password"
                  name="guardian1Password"
                  value={formData.guardian1Password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current password"
                />
                <small className="field-note">Enter new password only if you want to change it</small>
              </div>
            )}
          </div>
          
          {!isGuardian1 && playerData.guardian1Email && (
            <small className="field-note">Contact admin to update guardian information</small>
          )}
        </div>

        {/* Guardian 2 Information */}
        <div className="form-section">
          <h2>Guardian 2 Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={playerData.guardian2Name || 'Not set'}
                  disabled
                  className="readonly-field"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={playerData.guardian2Email || 'Not set'}
                  disabled
                  className="readonly-field"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={playerData.guardian2Phone || 'Not set'}
                  disabled
                  className="readonly-field"
                />
              </div>

              {isGuardian2 && (
                <div className="form-group">
                  <label htmlFor="guardian2Password">Change Your Password</label>
                  <input
                    type="password"
                    id="guardian2Password"
                    name="guardian2Password"
                    value={formData.guardian2Password}
                    onChange={handleChange}
                    placeholder="Leave blank to keep current password"
                  />
                  <small className="field-note">Enter new password only if you want to change it</small>
                </div>
              )}
            </div>
            
            {!isGuardian2 && (
              <small className="field-note">Contact admin to update guardian information</small>
            )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleCancel} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlayerProfile;
